import { Button } from 'components/Button';
import { CodeField } from 'components/Field/CodeField';
import { ICONS } from 'components/Icon';
import * as Clipboard from 'expo-clipboard';
import { useAlert } from 'hooks/useAlert';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { AUTHENTICATE, RESET_AUTH_STEP } from 'store/actions';

import { str } from 'lib/str';
import { Card } from 'components/Card';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { styles } from './styles';
import { UserChip } from './UserChip';
import { ButtonGroup } from 'components/ButtonGroup';

export const EmailValidate: React.FC = () => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const [code, setCode] = useState<string>('');
  const alert = useAlert();
  const autoFocusOnNextRenderRef = React.useRef<boolean>(true);

  const handleBack = () => dispatch(RESET_AUTH_STEP());

  const handleSubmit = useCallback(() => {
    if (code) {
      dispatch(AUTHENTICATE.request({ code }));
    }
  }, [code, dispatch]);

  const handlePaste = React.useCallback(async () => {
    const value = await Clipboard.getStringAsync();
    const cleanValue = value
      .split('')
      .filter((v) => Number.isInteger(parseInt(v, 10)))
      .join('')
      .slice(0, 6);
    if (cleanValue) {
      setCode(cleanValue);
    } else {
      alert({ text: 'Sorry, there was no code to paste' });
    }
  }, [alert]);

  const handleHelp = () => {
    alert({
      title: 'Having trouble with your code?',
      text:
        'A new *unique* code is sent **every** time you enter your email and press next so ' +
        'only use the most recent code.\n\n' +
        `The code won${str.apos}t work if you press back.\n\n` +
        //
        '*Code Is Wrong?*\n' +
        `If your code doesn${str.apos}t work, press back and enter your email again. ` +
        'Wait for the new unique code to arrive and enter that into RiskBase.\n\n' +
        //
        '*Code Not Received?*\n' +
        `Codes can take a few minutes to arrive; if you haven${str.apos}t received a code, ` +
        `check you${str.apos}ve entered the correct email address and check the email isn${str.apos}t in your junk folder.`,
      onConfirm: () => {
        forceUpdate();
      },
    });
    autoFocusOnNextRenderRef.current = false;
    forceUpdate();
  };

  useEffect(() => {
    if (code.length === 6) {
      handleSubmit();
    }
  }, [code, handleSubmit]);

  const autoFocus = autoFocusOnNextRenderRef.current;
  autoFocusOnNextRenderRef.current = true;

  return (
    <>
      <UserChip />
      <Card
        label={`We${str.apos}ve emailed you a 6 digit code\nPlease enter it below`}
        style={styles.textFieldContainer}
        labelLinkText="Help"
        onLabelLinkPress={handleHelp}
        explanationLinkText="Paste code from clipboard"
        onExplanationLinkPress={handlePaste}
      >
        <CodeField
          onChange={setCode}
          externalValue={code}
          // Will re-focus field if an API error comes back
          autoFocusEveryRender={autoFocus}
          key={autoFocus ? 'withAutoFocus' : 'withoutAutoFocus'}
        />
      </Card>
      <View style={styles.buttonContainer}>
        <ButtonGroup>
          <Button onPress={handleBack} text="Back" />
          <Button
            disabled={code.length < 6}
            icon={ICONS.ChevronRightSolid}
            onPress={handleSubmit}
            text="Next"
          />
        </ButtonGroup>
      </View>
    </>
  );
};
